import { useOldestMessages } from '../store/useMessageHooks';
import { useEffect } from 'react';
import { onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { Message } from '../../type';
import { useMessageCollection } from '../../../../firebase/useCollection';
import { useUserFetcherForMessages } from './useUserFetcher';
import { useCareTeam } from '../store/useCareTeam';
import { useStorageInfoFetcher } from './useStorageInfoFetcher';
import { QuerySnapshot, DocumentChange } from 'firebase/firestore';

export const useMessageListener = (channelGuid: string): void => {
  const collection = useMessageCollection(channelGuid);
  const [, dispatch] = useCareTeam();
  const enrichStorageInfo = useStorageInfoFetcher();
  const oldestMessage = useOldestMessages(channelGuid);
  const oldestMessageTimestamp = oldestMessage?.createdDate.toDate().getTime();
  const fetchUsers = useUserFetcherForMessages();

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    console.debug('Going to start message listener on channel', channelGuid);
    const fromDate = oldestMessageTimestamp
      ? new Date(oldestMessageTimestamp)
      : new Date();
    const messageQuery = query(
      collection,
      where('createdDate', '>=', fromDate),
      orderBy('createdDate', 'desc'),
      orderBy('authorGuid', 'asc'),
    );
    unsubscribe = onSnapshot(messageQuery, async (snapshot) => {
      const newMessages = await enrichMessageWithStorageInfo(
        snapshot,
        (doc) => doc.type === 'added',
        enrichStorageInfo,
      );
      const updatedMessages = await enrichMessageWithStorageInfo(
        snapshot,
        (doc) => doc.type === 'modified',
        enrichStorageInfo,
      );
      const users = await fetchUsers([...newMessages, ...updatedMessages]);
      dispatch({
        type: 'MESSAGE_UPDATE',
        channelGuid,
        newMessages,
        updatedMessages,
        users,
      });
    });
    return () => {
      if (unsubscribe) {
        console.debug(
          'Going to cancel message listener on channel',
          channelGuid,
        );
        unsubscribe();
      }
    };
  }, [
    channelGuid,
    collection,
    dispatch,
    oldestMessageTimestamp,
    fetchUsers,
    enrichStorageInfo,
  ]);
};

const enrichMessageWithStorageInfo = async (
  snapshot: QuerySnapshot,
  filter: (docChange: DocumentChange) => boolean,
  storageInfoFetcher: (messages: Message[]) => Promise<Message[]>,
): Promise<Message[]> => {
  const messages = snapshot
    .docChanges()
    .filter(filter)
    .map((doc) => doc.doc.data() as Message);
  return await storageInfoFetcher(messages);
};
